import { useState } from "react";
import { Scale, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { JurisprudenceDropdown } from "./JurisprudenceDropdown";
import type { ParsedJurisprudence } from "@/utils/jurisprudenceParser";

interface JurisprudenceCardListProps {
  jurisprudences: ParsedJurisprudence[];
  /** Nombre de fiches affichées avant repli. Défaut 5. */
  initialVisible?: number;
  className?: string;
}

/**
 * Liste de fiches d'arrêt avec repli progressif.
 *
 * WHY : plusieurs recherches JP parallèles peuvent renvoyer 20 → 100+ fiches. Les
 * afficher toutes d'un coup noie la réponse de l'agent et alourdit le scroll
 * (retour utilisateur 2026-06-18 : « c'est moche de voir 100 jurisprudences »).
 * On affiche les `initialVisible` premières — la liste arrive **déjà triée par
 * pertinence**, donc ce sont les plus utiles — et on déroule le reste à la
 * demande via « voir plus ». Aucune fiche n'est perdue, juste masquée par défaut.
 */
export function JurisprudenceCardList({
  jurisprudences,
  initialVisible = 5,
  className,
}: JurisprudenceCardListProps) {
  const [expanded, setExpanded] = useState(false);

  if (jurisprudences.length === 0) return null;

  const total = jurisprudences.length;
  const hasMore = total > initialVisible;
  const hiddenCount = total - initialVisible;
  const visible =
    expanded || !hasMore ? jurisprudences : jurisprudences.slice(0, initialVisible);

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-center gap-2 pb-1">
        <Scale className="w-3.5 h-3.5 text-grey-400" />
        <span className="text-xs font-semibold text-grey-400 uppercase tracking-widest">
          Fiches d'arrêt
        </span>
        <span className="text-xs font-medium text-grey-400">({total})</span>
      </div>

      {visible.map((jp) => (
        <JurisprudenceDropdown key={jp.id} jurisprudence={jp} />
      ))}

      {hasMore && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className={cn(
            "group flex w-full items-center justify-center gap-1.5 rounded-lg border border-grey-100 py-2",
            "text-xs font-semibold text-charcoal/70 transition-colors",
            "hover:border-grey-200 hover:bg-grey-50 hover:text-charcoal",
          )}
        >
          {expanded ? (
            <>
              Voir moins
              <ChevronUp className="w-3.5 h-3.5" />
            </>
          ) : (
            <>
              {`Voir ${hiddenCount > 1 ? `les ${hiddenCount} autres fiches` : "la fiche suivante"}`}
              <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:translate-y-0.5" />
            </>
          )}
        </button>
      )}
    </div>
  );
}
